/**
 * Sentry на фронте (01-SPEC.md §7, E16-T3).
 *
 * SDK грузится динамически и только при заданном `VITE_SENTRY_DSN`: иначе он
 * попал бы в основной бандл и утяжелил бы лендинг, который обязан держать
 * Lighthouse ≥ 90 (E15-T1).
 */
export async function initSentry(): Promise<void> {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) return;

  const Sentry = await import('@sentry/react');
  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    /*
     * Ни трассировки, ни записи сессий: в интерфейсе видны детские
     * фотографии и имена, и вывозить их наружу нельзя (§7, приватность).
     */
    tracesSampleRate: 0,
    sendDefaultPii: false,
    /*
     * The page URL travels with every browser event, and on the public book page it *is* the
     * access check: `/p/<token>` is the whole of it. Nothing an event is for needs the URL —
     * the error, the stack and the environment are the point — so it is replaced rather than
     * sent. The node services apply the same rule through `@kitup/providers/logging`; this
     * bundle must not load that module, so the rule is written out here.
     *
     * **Breadcrumbs are the same leak one array deeper.** The SDK's default integrations record
     * every fetch, and a breadcrumb's `data.url` holds the whole URL: a `/public/books/<token>`
     * request is a breadcrumb carrying the token, and a navigation breadcrumb carries `from`
     * and `to` instead. Redacting only `request.url` left those untouched.
     */
    beforeSend: (event) => {
      const scrubbed = { ...event };
      /*
       * The same rule as the node copy: a request field survives only if it has been reviewed.
       * `url` is redacted and the rest are dropped rather than kept, because the browser SDK's
       * request object is built from `location.href` and the node one carries the query string
       * **verbatim and without the leading `?`** in `query_string` — a field this SDK has no
       * equivalent of today, and one it would be redacted into if it ever gains one.
       */
      if (scrubbed.request !== undefined) {
        const request = scrubbed.request;
        scrubbed.request = {
          ...(request.method === undefined ? {} : { method: request.method }),
          ...(request.headers === undefined ? {} : { headers: request.headers }),
          ...(request.cookies === undefined ? {} : { cookies: request.cookies }),
          url: '[REDACTED]',
        };
      }
      const breadcrumbs = scrubbed.breadcrumbs;
      if (breadcrumbs !== undefined) {
        scrubbed.breadcrumbs = breadcrumbs.map((crumb) => {
          const data = crumb.data;
          if (data === undefined) return crumb;
          const cleaned = { ...data };
          /*
           * By name for the fields that are known, and by shape for the rest: the node SDK keeps
           * the query string in `data['http.query']` rather than in `data.url`, because `data.url`
           * is sanitised to scheme, host and path first. A value beginning with `?` or `#`, or a
           * URL carrying a query, is URL-derived whatever the field is called.
           */
          for (const [key, value] of Object.entries(cleaned)) {
            const urlDerived =
              typeof value === 'string' &&
              (value.startsWith('?') ||
                value.startsWith('#') ||
                (value.includes('://') && value.includes('?')));
            if (urlDerived || key === 'url' || key === 'from' || key === 'to') {
              cleaned[key] = '[REDACTED]';
            }
          }
          return { ...crumb, data: cleaned };
        });
      }
      return scrubbed;
    },
  });
}
